import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { ListResult } from '../../ts/fetchers/ListResult';
import { ITypedId, ITypedIdWithName } from '../../ts/ServerApi/types/ITypedId';
import { ITypedIds } from '../../ts/ServerApi/types/ITypedIds';
import { IGranteePermissions } from '../types/IGranteePermissions';
import { PermissionsHelper } from '../utils/PermissionsHelper';
import { PermissionsMerger } from '../utils/PermissionsMerger';
import { IGetExplicitPermissionsResult } from './types/GetExplicitPermissionsRequest';
import { ICapabilityValue } from './types/ICapabilityValue';

export interface ExplicitPermissionsResult extends IResult {
  permissionsList: ListResult<IGranteePermissions>;
  sourceAuthorizablesById: _.Dictionary<ITypedIdWithName>;
}

export function getExplicitPermissionsForAuthorizables(
  authorizables: ITypedIds
): Promise<ExplicitPermissionsResult> {
  const requests = PermissionsHelper.explicitPermissionsRequestsForAuthorizables(authorizables);
  const combineResultsFn = (results: IGetExplicitPermissionsResult[]) => {
    return {
      permissions: ResultHelper.combine(results, (result) => result.permissions),
      groups: ResultHelper.combine(results, (result) => result.groups),
      users: ResultHelper.combine(results, (result) => result.users),
      sourceAuthorizableById: ResultHelper.combineByKey(results, (result) => result.sourceAuthorizableById),
      errors: ResultHelper.combineErrors(results)
    };
  };

  return WebHttpClient.sendRequestsParallel(requests, combineResultsFn)
    .then<ExplicitPermissionsResult>((result: IGetExplicitPermissionsResult) => {
      const list = PermissionsMerger.makeGranteePermissionsList(
        result.permissions, authorizables.ids, result.groups, result.users
      );
      return {
        permissionsList: new ListResult<IGranteePermissions>(list),
        sourceAuthorizablesById: result.sourceAuthorizableById,
        errors: result.errors
      };
  });
}

// Assign permissions to the same grantee and multiple authorizables
export function setExplicitPermissionsForAuthorizables(
  authorizables: ITypedIds,
  granteePermissions: IGranteePermissions,
): Promise<IResult> {
  const requests = PermissionsHelper.setExplicitPermissionsRequestsForAuthorizables(authorizables, granteePermissions);

  return WebHttpClient.sendRequestsParallel(requests, ResultHelper.combineResults);
}

// Assign permissions to the same authorizable and multiple grantees
export function setExplicitPermissionsForGrantees(
  authorizable: ITypedId,
  grantees: ITypedId[],
  permissions: ICapabilityValue[]
): Promise<IResult> {
  const requests = PermissionsHelper.setExplicitPermissionsRequestsForGrantees(authorizable, grantees, permissions);

  return WebHttpClient.sendRequestsParallel(requests, ResultHelper.combineResults);
}
